import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { getAuthenticatedAxios } from './axiosConfig';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';
import { formatDate, formatDateTimeFull, formatDateOnly } from './Dateutils';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const SecurityCheckoutDashboard = () => {
    const { t } = useTranslation('securityCheckoutDashboard');
    const { currentUser } = useAuth();
    const [pendingCheckouts, setPendingCheckouts] = useState([]);
    const [selectedCheckout, setSelectedCheckout] = useState(null);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const [securityClearance, setSecurityClearance] = useState(false);
    const [notes, setNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchPendingCheckouts = async () => {
        try {
            setLoading(true);
            const axios = getAuthenticatedAxios();
            const response = await axios.get(`${BASE_URL}/checkout/pending-security`);

            const checkouts = Array.isArray(response.data) ? response.data : [];
            const validCheckouts = checkouts.filter(checkout =>
                checkout &&
                checkout.user_id &&
                (checkout.first_name || checkout.last_name)
            );

            setPendingCheckouts(validCheckouts);
            setError(null);
        } catch (err) {
            console.error('Error fetching pending checkouts:', err);
            setError(err.response?.data?.detail || t('errors.fetch_failed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser) {
            setError(t('errors.auth_required'));
            setLoading(false);
            return;
        }
        fetchPendingCheckouts();
        const pollingInterval = setInterval(fetchPendingCheckouts, 30000);
        return () => clearInterval(pollingInterval);
    }, [currentUser]);

    const handleProcessCheckout = (checkout) => {
        setSelectedCheckout(checkout);
        setSecurityClearance(false);
        setNotes('');
        setShowProcessModal(true);
    };

    const handleSubmitProcess = async () => {
        try {
            setIsProcessing(true);
            const authenticatedAxios = getAuthenticatedAxios();

            const processData = {
                user_id: selectedCheckout.user_id,
                notes: notes || t('process.default_notes'),
                badge_returned: securityClearance  // FIXED: schema expects badge_returned not security_clearance_granted
            };

            // FIXED: endpoint is /checkout/security-checkout not /checkout/security-process
            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/security-checkout`,
                processData
            );

            if (response.data) {
                // Notify staff + Security that Security stage is complete
                try {
                    await axios.post(`${BASE_URL}/notify/security-completed`, {
                        user_id: selectedCheckout.user_id,
                        notes: notes || null
                    });
                } catch (emailErr) {
                    console.warn('Security completion email failed:', emailErr);
                }

                // ADDED: Notify HR that checkout is now in their queue
                try {
                    await axios.post(`${BASE_URL}/notify/hr-pending`, {
                        user_id: selectedCheckout.user_id
                    });
                } catch (emailErr) {
                    console.warn('HR pending notification email failed:', emailErr);
                }

                setShowProcessModal(false);
                setShowConfirmModal(false);
                setSelectedCheckout(null);
                fetchPendingCheckouts();
                setError(null);
            }
        } catch (err) {
            console.error('Security Processing Error:', err);
            setError(err.response?.data?.detail || t('errors.process_failed'));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRejectCheckout = async () => {
        if (!rejectionReason.trim()) {
            setError('Please provide a reason for rejection.');
            return;
        }
        try {
            setIsProcessing(true);
            const authenticatedAxios = getAuthenticatedAxios();
            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/security-reject/${selectedCheckout.user_id}`,
                null,
                { params: { rejection_reason: rejectionReason.trim() } }
            );
            if (response.data) {
                setShowRejectModal(false);
                setShowProcessModal(false);
                setSelectedCheckout(null);
                setRejectionReason('');
                fetchPendingCheckouts();
                setError(null);
            }
        } catch (err) {
            console.error('Security Rejection Error:', err);
            setError(err.response?.data?.detail || 'Failed to reject checkout.');
        } finally {
            setIsProcessing(false);
        }
    };

    const canCompleteCheckout = () => securityClearance;

    const getEmployeeName = (checkout) => {
        if (!checkout) return t('table.unknown');
        return `${checkout.first_name || ''} ${checkout.last_name || ''}`.trim() || t('table.unknown');
    };

    return (
        <Container fluid className="mt-4">
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2>{t('heading')}</h2>
                        <Button variant="outline-primary" onClick={fetchPendingCheckouts} disabled={loading}>
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            {loading ? t('buttons.refreshing') : t('buttons.refresh')}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                    )}

                    {loading && pendingCheckouts.length === 0 ? (
                        <Alert variant="info">
                            <div className="d-flex align-items-center">
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">{t('loading.text')}</span>
                                </div>
                                {t('loading.message')}
                            </div>
                        </Alert>
                    ) : pendingCheckouts.length === 0 ? (
                        <Alert variant="success">
                            <i className="bi bi-check-circle me-2"></i>
                            {t('no_pending')}
                        </Alert>
                    ) : (
                        <>
                            <div className="mb-3">
                                <Row>
                                    <Col md={4}>
                                        <Card className="border-primary">
                                            <Card.Body>
                                                <h6 className="text-muted">{t('stats.pending')}</h6>
                                                <h2 className="mb-0">{pendingCheckouts.length}</h2>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>

                            <Table striped bordered hover responsive>
                                <thead>
                                <tr>
                                    <th>{t('table.headers.employee')}</th>
                                    <th>{t('table.headers.email')}</th>
                                    <th>ICT Completed</th>
                                    <th>{t('table.headers.initiated')}</th>
                                    <th>{t('table.headers.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {pendingCheckouts.map((checkout) => (
                                    <tr key={checkout.user_id}>
                                        <td>{getEmployeeName(checkout)}</td>
                                        <td>{checkout.email || t('table.no_email')}</td>
                                        <td>
                                            {checkout.ict_checkout_date ? (
                                                <span>
                                                    <Badge bg="success" className="me-2">✓ Completed</Badge>
                                                    <small className="text-muted">{formatDate(checkout.ict_checkout_date)}</small>
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td>{checkout.created_at ? formatDate(checkout.created_at) : t('table.date_not_available')}</td>
                                        <td>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleProcessCheckout(checkout)}
                                                disabled={false}
                                            >
                                                {t('buttons.process')}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Process Modal */}
            <Modal show={showProcessModal} onHide={() => setShowProcessModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('modal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Card className="mb-3">
                                <Card.Body>
                                    <h5>{t('modal.employee_info')}</h5>
                                    <p className="mb-1"><strong>{t('modal.name')}</strong> {getEmployeeName(selectedCheckout)}</p>
                                    <p className="mb-1"><strong>{t('modal.email')}</strong> {selectedCheckout.email}</p>
                                    <p className="mb-0"><strong>{t('modal.initiated')}</strong> {formatDate(selectedCheckout.created_at)}</p>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3 border-danger">
                                <Card.Header className="bg-danger text-white">
                                    <h6 className="mb-0">{t('verification.title')}</h6>
                                </Card.Header>
                                <Card.Body>
                                    <p className="text-muted mb-3">{t('verification.description')}</p>
                                    <ul className="mb-3">
                                        <li>{t('verification.items.badge')}</li>
                                        <li>{t('verification.items.keys')}</li>
                                        <li>{t('verification.items.access_cards')}</li>
                                        <li>{t('verification.items.parking')}</li>
                                        <li>{t('verification.items.clearance_docs')}</li>
                                    </ul>
                                    <Form.Group className="mb-0">
                                        <Form.Check
                                            type="checkbox"
                                            id="security-clearance"
                                            label={t('verification.clearance')}
                                            checked={securityClearance}
                                            onChange={(e) => setSecurityClearance(e.target.checked)}
                                        />
                                        <Form.Text className="text-muted">{t('verification.clearance_note')}</Form.Text>
                                    </Form.Group>
                                </Card.Body>
                            </Card>

                            <Form.Group>
                                <Form.Label>{t('form.notes_label')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder={t('form.notes_placeholder')}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={() => { setShowProcessModal(false); setShowRejectModal(true); }}>
                        {t('buttons.reject')}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowProcessModal(false)}>{t('buttons.cancel')}</Button>
                    <Button variant="success" onClick={() => setShowConfirmModal(true)} disabled={!canCompleteCheckout()}>
                        {t('buttons.complete')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirm Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('confirmation.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Alert variant="warning"><strong>{t('confirmation.warning')}</strong></Alert>
                            <div className="mb-3">
                                <h6>{t('confirmation.summary')}</h6>
                                <ul>
                                    <li><strong>{t('confirmation.employee')}</strong> {getEmployeeName(selectedCheckout)}</li>
                                    <li><strong>{t('confirmation.clearance')}</strong> {securityClearance ? t('confirmation.granted') : t('confirmation.not_granted')}</li>
                                    {notes && <li><strong>{t('confirmation.notes')}</strong> {t('confirmation.notes_provided')}</li>}
                                </ul>
                            </div>
                            {!securityClearance && (
                                <Alert variant="danger">
                                    <small>{t('confirmation.clearance_warning')}</small>
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>{t('buttons.back')}</Button>
                    <Button variant="primary" onClick={handleSubmitProcess} disabled={isProcessing}>
                        {isProcessing ? t('buttons.processing') : t('buttons.confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* Reject Modal */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">{t('rejection.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Alert variant="danger">
                                <strong>{t('rejection.warning')}</strong>
                            </Alert>
                            <p className="mb-3">
                                <strong>{t('rejection.employee')}</strong> {getEmployeeName(selectedCheckout)}
                            </p>
                            <Form.Group>
                                <Form.Label>{t('rejection.reason_label')} *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder={t('rejection.reason_placeholder')}
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    required
                                />
                                <Form.Text className="text-muted">
                                    {t('rejection.reason_note')}
                                </Form.Text>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleRejectCheckout}
                        disabled={isProcessing || !rejectionReason.trim()}
                    >
                        {isProcessing ? t('buttons.processing') : t('rejection.confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SecurityCheckoutDashboard;